import { useEffect, useState } from "react";
import NavBar from "@/components/NavBar";
import api from "@/lib/api";

const CONCERNS = [
  { key: "hyperpigmentation", label: "Hyperpigmentation — dark spots, post-acne marks, uneven tone" },
  { key: "redness", label: "Redness or rosacea-like flushing" },
  { key: "undereye_dark_circles", label: "Under-eye dark circles" },
  { key: "undereye_bags", label: "Under-eye bags / puffiness" },
  { key: "dullness_clogged_pores", label: "Dullness, clogged pores, blackheads or rough texture" },
];

export default function PrecisionCorrections() {
  const [data, setData] = useState(null);
  const [exfoliatorCriteria, setExfoliatorCriteria] = useState([]);
  const [exfChecked, setExfChecked] = useState({});

  // Gate state
  const [selected, setSelected] = useState({});
  const [submitted, setSubmitted] = useState(false);

  // Tracks which (concern_key, option_idx) pairs the user has added to their protocol
  const [addedKeys, setAddedKeys] = useState({});
  const [saving, setSaving] = useState({});

  useEffect(() => {
    api.get("/protocols/all").then((r) => setData(r.data.precision_corrections));
    api.get("/exfoliator-readiness-criteria").then((r) => setExfoliatorCriteria(r.data.criteria));
    // Preload current precision-additions + swaps so we can show "Added" state
    api.get("/precision-additions").then((r) => {
      const m = {};
      Object.keys(r.data || {}).forEach((k) => { m[k] = true; });
      setAddedKeys((prev) => ({ ...prev, ...m }));
    }).catch(() => {});
    api.get("/swaps").then((r) => {
      // The exfoliator option swaps the PM water cleanser slot; tag it as added
      const swaps = r.data || {};
      const water_cleanser_swap = swaps["making_actives_count:pm:1"];
      if (water_cleanser_swap && water_cleanser_swap.source === "precision_corrections") {
        const key = water_cleanser_swap.precision_option_key;
        if (key) setAddedKeys((prev) => ({ ...prev, [key]: true }));
      }
    }).catch(() => {});
  }, []);

  const addToProtocol = async (concernKey, optionIdx, option, concern) => {
    const localKey = `${concernKey}:${optionIdx}`;
    setSaving((s) => ({ ...s, [localKey]: true }));
    try {
      // Exfoliating cleanser is a special case — it REPLACES the PM water-cleanser slot.
      if (concern.options[optionIdx].replaces_step === "Water cleanser" || concernKey === "dullness_clogged_pores") {
        await api.put("/swaps", {
          slot_key: "making_actives_count:pm:1",
          product_name: option.product,
          product_url: option.url || "",
        });
      } else {
        // Other precision corrections — inject a new step in the right place
        const insertAfter = option.protocol_step === "Brightening / Hyperpigmentation"
          ? "Vitamin C"
          : option.protocol_step === "Eye cream"
            ? "Hydrating Serum"
            : "Vitamin C"; // default fallback for AM additions
        await api.put("/precision-additions", {
          concern_key: concernKey,
          option_idx: optionIdx,
          product_name: option.product,
          product_url: option.url || "",
          target_step: option.protocol_step || concern.title,
          target_time: option.protocol_time || "am",
          insert_after_step: insertAfter,
        });
      }
      setAddedKeys((m) => ({ ...m, [localKey]: true }));
    } catch (e) {
      // no-op
    } finally {
      setSaving((s) => ({ ...s, [localKey]: false }));
    }
  };

  if (!data) return <><NavBar /><div className="max-w-5xl mx-auto px-6 py-16 aa-eyebrow">Loading…</div></>;

  const anySelected = Object.values(selected).some(Boolean);
  const allChecked = exfoliatorCriteria.length > 0 && exfoliatorCriteria.every((_, i) => exfChecked[i]);
  const visibleConcerns = submitted
    ? Object.entries(data.concerns).filter(([k]) => selected[k])
    : [];
  const showsExfoliatorGate = submitted && selected.dullness_clogged_pores;

  return (
    <div className="App" data-testid="page-precision">
      <NavBar />
      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-14">
        <div className="aa-eyebrow mb-3">Precision Corrections</div>
        <h1 className="font-display text-4xl lg:text-5xl leading-tight">Targeted products for <span className="serif-italic">specific concerns</span>.</h1>
        <p className="mt-4 text-[var(--aa-text-2)] max-w-2xl">Precision Corrections are optional — only add them if you have one of the five concerns this phase targets. Let's check.</p>

        {/* GATE — concern selection */}
        {!submitted && (
          <div className="aa-card p-7 mt-10" data-testid="precision-gate">
            <div className="aa-eyebrow mb-4">Which of these apply to you?</div>
            <div className="space-y-3">
              {CONCERNS.map((c) => (
                <label key={c.key} className="flex gap-3 items-start cursor-pointer">
                  <input type="checkbox" data-testid={`gate-${c.key}`}
                    checked={!!selected[c.key]}
                    onChange={(e) => setSelected({ ...selected, [c.key]: e.target.checked })}
                    className="mt-1 h-5 w-5 accent-[var(--aa-text)]" />
                  <span className="text-[var(--aa-text-2)]">{c.label}</span>
                </label>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-6">
              <button data-testid="gate-submit" onClick={() => setSubmitted(true)}
                className="aa-cta-primary">
                {anySelected ? "Show me my options →" : "I have no concerns →"}
              </button>
              {!anySelected && <span className="text-xs text-[var(--aa-text-3)]">If nothing applies, you don't need Precision Corrections right now.</span>}
            </div>
          </div>
        )}

        {/* No concerns flow */}
        {submitted && !anySelected && (
          <div className="aa-card p-8 mt-10 fade-in" data-testid="precision-none">
            <div className="font-display text-2xl">You're good to go.</div>
            <p className="mt-3 text-[var(--aa-text-2)]">No Precision Corrections are needed right now. Keep going with your current routine — and come back here any time if a specific concern shows up.</p>
            <button onClick={() => { setSubmitted(false); setSelected({}); }} data-testid="precision-restart" className="aa-cta-secondary mt-5">← Retake</button>
          </div>
        )}

        {/* Filtered concerns */}
        {submitted && anySelected && (
          <>
            <div className="flex items-center justify-between mt-10 flex-wrap gap-3">
              <h2 className="font-display text-2xl">Your selected concerns</h2>
              <button onClick={() => { setSubmitted(false); }} data-testid="precision-back" className="aa-cta-secondary">← Change selection</button>
            </div>
            <div className="grid md:grid-cols-2 gap-5 mt-6">
              {visibleConcerns.map(([key, c]) => (
                <div key={key} className="aa-card p-6" data-testid={`pc-${key}`}>
                  <div className="flex items-baseline justify-between flex-wrap gap-2 mb-2">
                    <div className="font-display text-xl">{c.title}</div>
                    {!c.available_during_mac && <span className="aa-pill verdict-yellow text-xs">Gated</span>}
                  </div>
                  <p className="text-sm text-[var(--aa-text-2)] mb-4">{c.summary}</p>
                  <ul className="space-y-3">
                    {c.options.map((o, i) => (
                      <li key={i} className="border-t border-[var(--aa-elevated)] pt-3">
                        <div className="aa-eyebrow mb-1">{o.label}</div>
                        {o.url ? (
                          <a href={o.url} target="_blank" rel="noopener noreferrer" className="font-display hover:underline">{o.product}</a>
                        ) : (
                          <div className="font-display">{o.product}</div>
                        )}
                        <div className="text-sm text-[var(--aa-text-2)] mt-1 leading-relaxed">{o.when}</div>
                        {c.available_during_mac && (
                          <button
                            data-testid={`pc-add-${key}-${i}`}
                            onClick={() => addToProtocol(key, i, o, c)}
                            disabled={!!addedKeys[`${key}:${i}`] || saving[`${key}:${i}`]}
                            className="aa-cta-secondary mt-3 text-xs"
                          >
                            {addedKeys[`${key}:${i}`] ? "✓ Added to my protocol" : saving[`${key}:${i}`] ? "Adding…" : "Add to my protocol →"}
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                  {c.gated_note && <div className="mt-4 text-sm italic text-[var(--aa-text-3)] leading-relaxed">{c.gated_note}</div>}
                </div>
              ))}
            </div>

            {/* Exfoliator gate only shown if Dullness selected */}
            {showsExfoliatorGate && (
              <div className="mt-14">
                <h2 className="font-display text-3xl">Am I ready for an exfoliator?</h2>
                <p className="text-[var(--aa-text-2)] mt-3 max-w-2xl">Exfoliating cleansers are gated. Hold off through Skin Resilience, Making Actives Count, and retinoid optimization. Once all four are true, you can begin.</p>
                <div className="aa-card p-7 mt-6 space-y-4" data-testid="exf-checklist">
                  {exfoliatorCriteria.map((c, i) => (
                    <label key={i} className="flex gap-3 items-start cursor-pointer">
                      <input type="checkbox" data-testid={`exf-check-${i}`} checked={!!exfChecked[i]} onChange={(e) => setExfChecked({ ...exfChecked, [i]: e.target.checked })}
                        className="mt-1 h-5 w-5 accent-[var(--aa-text)]" />
                      <span className="text-[var(--aa-text-2)]">{c}</span>
                    </label>
                  ))}
                  <div className={`aa-pill mt-3 ${allChecked ? "verdict-green" : "verdict-yellow"}`} data-testid="exf-status">
                    {allChecked ? "✓ Ready. Start your exfoliating cleanser no more than 2 nights/week; increase by 1 night every 2 weeks as tolerated. Avoid scrubs/microbeads." : "Not yet — keep going until all 4 are true."}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
